<script lang="ts">
  /**
   * Toast viewport, application stack — 03-components/11-toasts · OSCloud DS 1.1.151
   *
   * The one global stack an app shell mounts: a single body-level root, `position: fixed`, so
   * it never scrolls away and no ancestor overflow can clip it. It reads the notification store
   * and renders only the entries raised with `asToast`, which is how a module opts into the
   * design system's toast instead of `IndexNotification`'s banner — the two never double up on
   * the same message.
   *
   * The card's five `data-position` corners are the showcase's; the client area has one stack,
   * and it takes no position.
   *
   * AppLayout mounts it for every page, so a message raised anywhere (a lost connection, say)
   * has a stack to land in. Pages that still mount their own copy are harmless: only the
   * earliest-mounted live instance draws, so a toast is never rendered twice.
   */
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

  import BfToast from '@/components/ui/bahnhof/BfToast.vue'
  import { useNotificationStore } from '@/store/notification'
  import type { NotificationItemI } from '@/typescript/types/stores/NotificationStore'

  /** The card's own limit: beyond five the stack stops being readable. */
  const TOAST_LIMIT = 5

  /** Mounted instances, oldest first; the head is the one that draws. Shared across instances. */
  const instances = ref<symbol[]>([])
</script>

<script setup lang="ts">
  const notificationStore = useNotificationStore()

  const instance = Symbol('BfToastViewport')
  const isActive = computed<boolean>(() => instances.value[0] === instance)

  onMounted(() => {
    instances.value.push(instance)
  })

  onBeforeUnmount(() => {
    instances.value = instances.value.filter(item => item !== instance)
  })

  const toasts = computed<NotificationItemI[]>(() => notificationStore.getNotificationList.filter(item => item.asToast))

  const visible = computed<NotificationItemI[]>(() => toasts.value.slice(-TOAST_LIMIT))

  /**
   * The overflow is dropped from the store rather than merely hidden: a toast that is never
   * rendered has no countdown ring, so nothing would ever dismiss it and it would sit in the
   * list forever.
   */
  watch(toasts, list => {
    if (!isActive.value) return
    list.slice(0, Math.max(0, list.length - TOAST_LIMIT)).forEach(item => notificationStore.removeNotification(item.id))
  })

  /**
   * The store's `withTimer` is the banner's opt-in to a countdown, because a banner can also
   * stay until it is dismissed. A toast is transient by definition — the ring is the component
   * — so the flag does not reach here and every toast takes the card's duration.
   */
  const DURATION = 4500

  type BfToastType = 'neutral' | 'info' | 'success' | 'warning' | 'error'
  const TYPES: BfToastType[] = ['neutral', 'info', 'success', 'warning', 'error']

  /** The store's `type` is a free string; anything the card does not draw falls back to neutral. */
  const toastType = (item: NotificationItemI): BfToastType =>
    TYPES.includes(item.type as BfToastType) ? (item.type as BfToastType) : 'neutral'
</script>

<template>
  <Teleport to="body">
    <TransitionGroup
      v-if="isActive && visible.length"
      name="toast-top"
      tag="div"
      class="toast-viewport--application"
      role="region"
      aria-label="Notifications"
    >
      <BfToast
        v-for="item in visible"
        :key="item.id"
        :type="toastType(item)"
        :message="item.content"
        :description="item.description"
        :duration="DURATION"
        @close="notificationStore.removeNotification(item.id)"
      />
    </TransitionGroup>
  </Teleport>
</template>

<style scoped lang="scss">
  /* Centred on the PAGE CONTENT REGION rather than on the window.
     `left` + `right: 0` + `max-width` is over-constrained, so `right` is dropped: the box stays
     pinned to the start of the content region and caps at --bf-page-content-width, exactly like
     BfPageLayout, which has no auto inline margins either — `align-items` then centres the
     toasts on the true content centre. The width bounds move from the viewport to the toasts,
     since the viewport is now the whole column.

     Where the region begins comes from --toast-viewport-inset-start, published at :root by
     BfConsoleShell (220px expanded, 64px in rail, 0 in the drawer state). Default 0, so a page
     with no sidebar centres on the window, which is correct there. */
  .toast-viewport--application {
    position: fixed;
    top: calc(var(--bf-header-h-app, 0px) + 16px);
    left: var(--toast-viewport-inset-start, 0px);
    right: 0;
    bottom: auto;
    width: auto;
    max-width: var(--bf-page-content-width);
    padding-inline: var(--bf-page-gutter); /* edge breathing room only — a gutter does not move a centre */
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    /* Above the DynamicModal overlay (10000): a toast raised from inside a modal — a copied
       backup code, say — must not land under its backdrop. */
    z-index: 10001;
    pointer-events: none;

    > * {
      /* DS 1.1.130 — the floor rose with the trailing padding, so the text measure is
         unchanged. */
      width: max-content;
      min-width: 320px;
      max-width: min(420px, 100%);
      pointer-events: auto;
    }
  }

  /* The stack hangs from the top edge, so its toasts both enter and leave through it — the
     card's own top transition pair. Only opacity and transform are transitioned: `all` would
     also animate the countdown ring's stroke and the tone colours. */
  .toast-top-enter-active {
    transition:
      opacity 220ms cubic-bezier(0.2, 0.8, 0.2, 1),
      transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  .toast-top-leave-active {
    transition:
      opacity 160ms ease-in,
      transform 160ms ease-in;
  }

  .toast-top-enter-from {
    opacity: 0;
    transform: translateY(-16px);
  }

  .toast-top-leave-to {
    opacity: 0;
    transform: translateY(-12px);
  }

  @media (prefers-reduced-motion: reduce) {
    .toast-top-enter-active,
    .toast-top-leave-active {
      transition: none;
    }
  }
</style>
